import React, { Suspense, useEffect } from 'react'
import ViewSelector, {
  OptionKey,
} from '../../../stories/ViewSelector/ViewSelector'
import { useMediaQuery } from '@mantine/hooks'
import KpiTabsContainer from '../../../widgets/components/KpiTabsContainer'
import PeriodTable from '../../../widgets/components/PeriodTable'
import {
  WidgetContextProvider,
  useWidgetContext,
} from '../../../widgets/providers'
import { Center, Group, Loader } from '@mantine/core'
import WaterfallChart from '../../../widgets/components/WaterfallChart'
import classes from './PerformanceSection.module.css'
import LineChart from '../../../widgets/components/LineChart'
import clsx from 'clsx'
import BarChart from '../../../widgets/components/BarChart'
import { Dropdown } from '../dropdown/Dropdown'
import { useMetrics } from '../../../widgets/api'

export default function PerformanceSection() {
  const [view, setView] = React.useState<OptionKey>('line')
  const [component, setComponent] = React.useState<string>('percent')
  const isDesktop = useMediaQuery('(min-width: 800px)')

  return (
    <div className={classes.player_performance}>
      <KpiTabsContainer
        title="Performance"
        id="performance_kpis"
        subheading={
          <ViewControls
            view={view}
            setView={setView}
            component={component}
            setComponent={setComponent}
            isDesktop={isDesktop}
          />
        }
      >
        <Block className={classes.widgets}>
          <Group grow align="stretch" gap="xs" style={{ minHeight: '300px' }}>
            <Suspense
              fallback={
                <Center>
                  <Loader />
                </Center>
              }
            >
              {(isDesktop || view === 'table') && (
                <PeriodTable id="performance_periodtable" />
              )}
              {view === 'bar' && (
                <WidgetContextProvider
                  context={{
                    component,
                    statistic: 'value',
                  }}
                >
                  <BarChart id="performance_barchart" />
                </WidgetContextProvider>
              )}
              {view === 'waterfall' && (
                <WaterfallChart id="performance_waterfall" />
              )}
              {view === 'line' && (
                <WidgetContextProvider
                  context={{
                    component,
                    statistic:
                      component === 'score' && view === 'line'
                        ? 'value_ytd'
                        : 'value',
                  }}
                >
                  <LineChart id="performance_linechart" />
                </WidgetContextProvider>
              )}
            </Suspense>
          </Group>
        </Block>
      </KpiTabsContainer>
    </div>
  )
}

function ViewControls({
  view,
  setView,
  component,
  setComponent,
  isDesktop,
}: {
  view: OptionKey
  setView: (v: OptionKey) => void
  component: string
  setComponent: (v: string) => void
  isDesktop: boolean | undefined
}) {
  const ctx = useWidgetContext()
  const { data: metrics } = useMetrics()
  const metric = metrics?.find((m) => m.key === ctx.metric)
  const isPercent = metric?.is_percent_metric ?? false

  useEffect(() => {
    if (isPercent && view === 'waterfall') {
      setView('bar')
    }
  }, [isPercent, view, setView])

  const components = [
    { label: 'Target (%)', value: 'percent' },
    { label: 'Actual', value: 'actual' },
    { label: view === 'line' ? 'Score YTD' : 'Score', value: 'score' },
  ]

  const enabledViews: OptionKey[] = isDesktop
    ? isPercent
      ? ['line', 'bar']
      : ['line', 'bar', 'waterfall']
    : isPercent
      ? ['table', 'line', 'bar']
      : ['table', 'line', 'bar', 'waterfall']

  return (
    <Block>
      <Group
        wrap="nowrap"
        align="center"
        justify={isDesktop ? 'flex-end' : 'flex-start'}
        pr="md"
        pl="md"
      >
        <Group wrap="nowrap" align="center" gap="xs">
          <ViewSelector
            value={view}
            onChange={setView}
            enabled={enabledViews}
          />
          <Dropdown
            placeholder="Select component"
            disabled={view === 'waterfall'}
            data={components}
            style={{ maxWidth: '150px' }}
            value={component}
            onChange={(value) => setComponent(value || 'percent')}
            styles={{
              root: { maxWidth: '150px' },
              input: { backgroundColor: 'transparent' },
            }}
          />
        </Group>
      </Group>
    </Block>
  )
}

// TODO move to a common location and reuse?
export function Block(props: {
  children: React.ReactNode
  className?: string
}) {
  return (
    <div className={clsx(classes.block, props.className)}>{props.children}</div>
  )
}
